import { Cloud, CloudOff, RefreshCw, Trash2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useOfflineQueue } from '@/hooks/useOfflineQueue';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { ScrollArea } from '@/components/ui/scroll-area';

export function OfflineQueueIndicator() {
  const { queue, pendingCount, failedCount, isSyncing, syncQueue, clearQueue, retryFailed } = useOfflineQueue();
  const isOnline = navigator.onLine;

  if (pendingCount === 0 && failedCount === 0) return null;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            "gap-2",
            failedCount > 0 && "border-destructive text-destructive"
          )}
        >
          {isOnline ? (
            <Cloud className="h-4 w-4" />
          ) : (
            <CloudOff className="h-4 w-4" />
          )}
          <span className="text-xs">
            {pendingCount > 0 && `${pendingCount} pending`}
            {failedCount > 0 && ` • ${failedCount} failed`}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="end">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold">Offline Queue</h4>
            <div className="flex gap-2">
              {failedCount > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={retryFailed}
                  disabled={isSyncing}
                >
                  <RefreshCw className="h-3 w-3 mr-1" />
                  Retry
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={clearQueue}
                disabled={isSyncing}
              >
                <Trash2 className="h-3 w-3 mr-1" />
                Clear
              </Button>
            </div>
          </div>

          {!isOnline && (
            <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
              <CloudOff className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                You're offline. Changes will sync when connected.
              </span>
            </div>
          )}

          {isOnline && pendingCount > 0 && (
            <Button
              onClick={syncQueue}
              disabled={isSyncing}
              className="w-full"
            >
              {isSyncing ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Syncing...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Sync Now
                </>
              )}
            </Button>
          )}

          <ScrollArea className="h-[300px]">
            <div className="space-y-2">
              {queue.map((action) => (
                <div
                  key={action.id}
                  className={cn(
                    "p-3 rounded-lg border text-sm",
                    action.status === 'failed' && "border-destructive bg-destructive/5"
                  )}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Badge variant={action.status === 'failed' ? 'destructive' : 'secondary'}>
                          {action.type}
                        </Badge>
                        <span className="font-medium capitalize">{action.entity}</span>
                      </div>
                      {action.status === 'failed' && action.error && (
                        <div className="flex items-center gap-1 mt-2 text-xs text-destructive">
                          <AlertCircle className="h-3 w-3" />
                          <span>{action.error}</span>
                        </div>
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {new Date(action.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>
      </PopoverContent>
    </Popover>
  );
}
